import { Capacitor } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';

/**
 * Mode application (Capacitor, iOS / Android). Dans l'app, le SPA est servi
 * depuis l'appareil : l'API est appelée en absolu, avec un jeton Bearer gardé
 * dans le trousseau (iOS Keychain, Android Keystore), jamais en localStorage.
 * Sur le web, rien ne change : cookie HTTP-only, même origine.
 */
export const isNativeApp = Capacitor.isNativePlatform();
export const nativePlatform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

/** Origine de l'API pour l'app ; vide sur le web (même origine). */
export const API_ORIGIN = isNativeApp
  ? (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? 'https://yakafa.fr'
  : '';

const TOKEN_KEY = 'session-token';
let token: string | null = null;
let loaded = false;

export async function loadSessionToken(): Promise<string | null> {
  if (!isNativeApp || loaded) return token;
  loaded = true;
  try {
    token = ((await SecureStorage.get(TOKEN_KEY)) as string | null) ?? null;
  } catch {
    token = null;
  }
  return token;
}

export const sessionToken = (): string | null => token;

export async function saveSessionToken(value: string): Promise<void> {
  token = value;
  await SecureStorage.set(TOKEN_KEY, value);
}

export async function clearSessionToken(): Promise<void> {
  token = null;
  await SecureStorage.remove(TOKEN_KEY).catch(() => undefined);
}

let deviceName: string | null = null;
/** « iPhone 15 », « Pixel 8 »… : aide l'utilisateur à reconnaître ses sessions. */
export async function loadDeviceName(): Promise<void> {
  if (!isNativeApp) return;
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    deviceName = `${info.manufacturer} ${info.model}`.trim().slice(0, 120);
  } catch {
    deviceName = null;
  }
}

/** En-têtes propres à l'app : l'API y répond par un jeton plutôt qu'un cookie. */
export function nativeHeaders(): Record<string, string> {
  if (!isNativeApp) return {};
  return {
    'x-novofa-client': nativePlatform,
    ...(deviceName ? { 'x-novofa-device': deviceName } : {}),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}
